import React from 'react';

import {
    Flex, Box, Divider, Text, Link, Image, Grid
} from 'components/ui';
import { space, colors, fontSizes } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';

const ORDERS_URL = '/profile/MyAccount/Orders';

/**
 * Displays a summary of auto-replenishment subscriptions with status counts and benefit metrics
 *
 * @param {Object} props - Component props
 * @param {Object} props.localization - Localized text strings
 * @param {string} props.localization.autoReplenishSummary - Summary section title
 * @param {string} props.localization.viewShipments - View shipments link text
 * @param {Array<{title: string, value: number}>} props.statuses - Subscription status counts (active, paused, cancelled)
 * @param {Array<{title: string, src: string, value: string}>} props.items - Benefit items (BI points, savings)
 * @param {boolean} props.showBottomViewShipments - Whether to show the view shipments link at the bottom
 * @returns {React.JSX.Element}
 */
function AutoReplenishmentSummary({ localization, statuses, items, showBottomViewShipments }) {
    return (
        <Box css={styles.container}>
            <Grid css={styles.content}>
                <Box>
                    <div css={styles.title}>{localization.autoReplenishSummary}</div>
                    <div css={styles.status}>
                        {statuses?.map((status, index) => (
                            <React.Fragment key={status.title}>
                                {index > 0 && <span css={styles.separator}>|</span>}
                                {`${status.title} (${status.value})`}
                            </React.Fragment>
                        ))}
                    </div>
                </Box>
                {!showBottomViewShipments && (
                    <Flex>
                        <Link
                            css={{ ...styles.link, ...styles.vAlignMiddle }}
                            href={ORDERS_URL}
                            children={localization.viewShipments}
                        />
                    </Flex>
                )}
            </Grid>
            <Divider />
            <Grid css={styles.content}>
                <Box css={styles.items}>
                    {items?.map(item => (
                        <div
                            css={styles.item}
                            key={item.title}
                        >
                            <Image
                                css={styles.itemIcon}
                                height={25}
                                src={item.src}
                            />
                            <Text is='p'>{item.title}</Text>
                            <Text
                                is='span'
                                css={styles.itemValue}
                            >
                                <strong>{item.value}</strong>
                            </Text>
                        </div>
                    ))}
                </Box>
            </Grid>

            {showBottomViewShipments && (
                <>
                    <Divider />
                    <Flex>
                        <Link
                            css={{ ...styles.link, ...styles.hAlignLeft }}
                            href={ORDERS_URL}
                            children={localization.viewShipments}
                        />
                    </Flex>
                </>
            )}
        </Box>
    );
}

const styles = {
    container: {
        backgroundColor: `${colors.nearWhite}`,
        padding: `${space[1]}px ${space[4]}px 0 ${space[4]}px`,
        marginTop: `${space[4]}px`,
        borderRadius: `${space[1]}px`
    },
    content: {
        gap: 0,
        gridTemplateColumns: '1fr auto',
        padding: `${space[3]}px 0`
    },
    title: {
        marginBottom: `${space[1]}px`
    },
    status: {
        fontSize: `${fontSizes['sm']}px`
    },
    separator: {
        margin: `0 ${space[2]}px`,
        color: `${colors.midGray}`
    },
    items: {
        display: 'flex',
        flexDirection: 'column',
        gap: `${space[3]}px`
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        fontSize: `${fontSizes['sm']}px`
    },
    itemIcon: {
        verticalAlign: 'middle',
        marginRight: `${space[3]}px`,
        flexShrink: 0
    },
    itemValue: {
        textAlign: 'right',
        marginLeft: 'auto',
        wordWrap: 'normal'
    },
    link: {
        color: `${colors.blue}`,
        margin: `${space[3]}px 0`
    },
    vAlignMiddle: {
        margin: 'auto'
    },
    hAlignLeft: {
        margin: `${space[3]}px 0`
    }
};

export default wrapFunctionalComponent(AutoReplenishmentSummary, 'AutoReplenishmentSummary');
